import { useCallback, useEffect, useMemo, useState } from 'react'
import { signInWithCustomToken } from 'firebase/auth'
import { collection, getDocs, orderBy, query, QueryConstraint, where } from 'firebase/firestore'
import { useSelector } from 'react-redux'
import { selectCredentials } from '@views/loginView/state/loginSlice'
import { userProfile } from '@views/profile/state/profileSlice'
import { Role } from '@types'
import {
  bootstrapAdminHoursMetricsService,
  getAdminMetricsEntriesService,
  getAdminMetricsPausesService,
  getEmployeesByIdsService,
  searchEmployeesByNameService
} from '@http/adminMetricsService'
import { getFirebaseClients, isFirebaseConfigured, signOutOfficeTracker } from '@officeTimeTracker/firebase/client'
import {
  formatLocalDateKey,
  formatSeconds,
  getCurrentLocalTime,
  getErrorMessage,
  normalizeRole,
  TRACKER_ROLES
} from '@officeTimeTracker/utils/timeTrackerUi'
import { TrackerPause } from '@officeTimeTracker/types'
import { AdminMetricsEntry, BasicEmployee, EmployeeSummary, MetricsPeriodMode } from '../types'

interface EmployeeSummaryAccumulator extends EmployeeSummary {
  workDates: Set<string>
}

const getWeekStartDate = (dateKey: string) => {
  const referenceDate = new Date(`${dateKey}T12:00:00-05:00`)
  const localDay = referenceDate.getUTCDay()
  const mondayOffset = localDay === 0 ? -6 : 1 - localDay
  const mondayDate = new Date(referenceDate.getTime())
  mondayDate.setUTCDate(mondayDate.getUTCDate() + mondayOffset)
  return formatLocalDateKey(mondayDate)
}

const getMonthRange = (monthValue: string) => {
  const [year, month] = monthValue.split('-').map(Number)
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()

  return {
    startDate: `${monthValue}-01`,
    endDate: `${monthValue}-${String(lastDay).padStart(2, '0')}`
  }
}

const sortEntries = (entries: AdminMetricsEntry[]) =>
  [...entries].sort((left, right) => {
    const leftTime = new Date(left.startedAt).getTime()
    const rightTime = new Date(right.startedAt).getTime()
    return leftTime - rightTime
  })

const createFallbackEmployee = (userId: string): BasicEmployee => ({
  _id: userId,
  name: `Empleado ${userId.slice(-6)}`,
  role: 'Sin datos'
})

const useAdminHoursMetrics = (enabled = true) => {
  const { cocap } = useSelector(selectCredentials)
  const { role } = useSelector(userProfile)
  const todayKey = useMemo(() => formatLocalDateKey(getCurrentLocalTime()), [])
  const [mode, setMode] = useState<MetricsPeriodMode>('week')
  const [referenceDate, setReferenceDate] = useState(todayKey)
  const [referenceMonth, setReferenceMonth] = useState(todayKey.slice(0, 7))
  const [entries, setEntries] = useState<AdminMetricsEntry[]>([])
  const [employeeMap, setEmployeeMap] = useState<Record<string, BasicEmployee>>({})
  const [selectedEmployee, setSelectedEmployee] = useState<BasicEmployee | null>(null)
  const [selectedEmployeePausesByEntryId, setSelectedEmployeePausesByEntryId] = useState<
    Record<string, TrackerPause[]>
  >({})
  const [searchTerm, setSearchTerm] = useState('')
  const [searchFilterUserIds, setSearchFilterUserIds] = useState<string[] | null>(null)
  const [bootstrapping, setBootstrapping] = useState(false)
  const [loadingEntries, setLoadingEntries] = useState(false)
  const [searching, setSearching] = useState(false)
  const [loadingSelectedEmployeePauses, setLoadingSelectedEmployeePauses] = useState(false)
  const [bootstrapReady, setBootstrapReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resetSearch = useCallback(() => {
    setSearchTerm('')
    setSearchFilterUserIds(null)
    setSelectedEmployee(null)
    setSelectedEmployeePausesByEntryId({})
  }, [])

  const selectEmployee = useCallback((employee: BasicEmployee) => {
    setSelectedEmployee(employee)
    setSearchTerm(employee.name)
    setEmployeeMap(previousEmployeeMap => ({
      ...previousEmployeeMap,
      [employee._id]: employee
    }))
  }, [])

  const hydrateEmployees = useCallback(
    async (loadedEntries: AdminMetricsEntry[]) => {
      const uniqueUserIds = Array.from(new Set(loadedEntries.map(entry => entry.userId)))
      if (!uniqueUserIds.length) {
        setEmployeeMap(previousEmployeeMap => {
          if (selectedEmployee?._id) {
            return {
              ...previousEmployeeMap,
              [selectedEmployee._id]: selectedEmployee
            }
          }

          return previousEmployeeMap
        })
        return
      }

      const employeesResponse = await getEmployeesByIdsService(uniqueUserIds, cocap)
      const nextEmployees = employeesResponse.result.reduce<Record<string, BasicEmployee>>((accumulator, employee) => {
        accumulator[String(employee._id)] = employee
        return accumulator
      }, {})

      if (selectedEmployee?._id) {
        nextEmployees[selectedEmployee._id] = selectedEmployee
      }

      setEmployeeMap(previousEmployeeMap => ({
        ...previousEmployeeMap,
        ...nextEmployees
      }))
    },
    [cocap, selectedEmployee]
  )

  useEffect(() => {
    if (!enabled || role !== Role.admin || !cocap) {
      return undefined
    }

    let isMounted = true

    const bootstrap = async () => {
      if (!isFirebaseConfigured()) {
        setBootstrapReady(false)
        return
      }

      setBootstrapping(true)

      try {
        const response = await bootstrapAdminHoursMetricsService(cocap)
        if (!response.featureEnabled || !response.firebaseCustomToken) {
          throw new Error('El backend no habilitó el acceso administrativo a métricas.')
        }

        const { clientAuth } = getFirebaseClients()
        await signInWithCustomToken(clientAuth, response.firebaseCustomToken)

        if (!isMounted) {
          return
        }

        setBootstrapReady(true)
      } catch (bootstrapError) {
        if (!isMounted) {
          return
        }

        setBootstrapReady(false)
        console.warn('Admin metrics Firebase bootstrap failed, using backend fallback', bootstrapError)
      } finally {
        if (isMounted) {
          setBootstrapping(false)
        }
      }
    }

    void bootstrap()

    return () => {
      isMounted = false
      void signOutOfficeTracker()
    }
  }, [cocap, enabled, role])

  useEffect(() => {
    if (!enabled || role !== Role.admin || !cocap) {
      return undefined
    }

    let isMounted = true

    const loadEntries = async () => {
      setLoadingEntries(true)
      setError(null)

      try {
        const loadEntriesFromBackend = async () => {
          const response = await getAdminMetricsEntriesService(
            {
              mode,
              referenceDate,
              referenceMonth
            },
            cocap
          )

          return sortEntries(response.entries)
        }

        let sortedEntries: AdminMetricsEntry[] = []

        if (bootstrapReady) {
          try {
            const { clientFirestore } = getFirebaseClients()
            const filters: QueryConstraint[] = []

            if (mode === 'day') {
              filters.push(where('workDate', '==', referenceDate))
            }

            if (mode === 'week') {
              filters.push(where('weekStartDate', '==', getWeekStartDate(referenceDate)))
            }

            if (mode === 'month') {
              const { startDate, endDate } = getMonthRange(referenceMonth)
              filters.push(where('workDate', '>=', startDate))
              filters.push(where('workDate', '<=', endDate))
              filters.push(orderBy('workDate', 'asc'))
            }

            const snapshot = await getDocs(query(collection(clientFirestore, 'time_entries'), ...filters))
            const snapshotEntries = snapshot.docs
              .map(document => document.data() as AdminMetricsEntry)
              .filter(entry => Boolean(entry.userId) && !String(entry.userId).includes(':'))

            sortedEntries = sortEntries(snapshotEntries)
          } catch (firestoreError) {
            const message = getErrorMessage(firestoreError, 'Firestore query failed')
            const shouldFallback =
              message.includes('Missing or insufficient permissions') || message.toLowerCase().includes('permission')

            if (!shouldFallback) {
              throw firestoreError
            }

            sortedEntries = await loadEntriesFromBackend()
          }
        } else {
          sortedEntries = await loadEntriesFromBackend()
        }

        if (!isMounted) {
          return
        }

        setEntries(sortedEntries)
        await hydrateEmployees(sortedEntries)
      } catch (loadError) {
        if (!isMounted) {
          return
        }

        setEntries([])
        setError(getErrorMessage(loadError, 'No se pudieron cargar las horas registradas.'))
      } finally {
        if (isMounted) {
          setLoadingEntries(false)
        }
      }
    }

    void loadEntries()

    return () => {
      isMounted = false
    }
  }, [bootstrapReady, cocap, enabled, hydrateEmployees, mode, referenceDate, referenceMonth, role, selectedEmployee])

  const searchEmployees = useCallback(async () => {
    const trimmedSearch = searchTerm.trim()
    if (!trimmedSearch) {
      setSearchFilterUserIds(null)
      return
    }

    setSearching(true)
    setError(null)

    try {
      const response = await searchEmployeesByNameService(trimmedSearch, cocap)
      const eligibleEmployees = response.result.filter(employee => {
        const normalizedRole = normalizeRole(employee.role)
        return Boolean(normalizedRole && TRACKER_ROLES.has(normalizedRole))
      })

      setEmployeeMap(previousEmployeeMap => ({
        ...previousEmployeeMap,
        ...eligibleEmployees.reduce<Record<string, BasicEmployee>>((accumulator, employee) => {
          accumulator[employee._id] = employee
          return accumulator
        }, {})
      }))
      setSearchFilterUserIds(eligibleEmployees.map(employee => employee._id))

      if (selectedEmployee && !eligibleEmployees.some(employee => employee._id === selectedEmployee._id)) {
        setSelectedEmployee(null)
      }
    } catch (searchError) {
      setError(getErrorMessage(searchError, 'No se pudo buscar el empleado.'))
      setSearchFilterUserIds(null)
    } finally {
      setSearching(false)
    }
  }, [cocap, searchTerm, selectedEmployee])

  const visibleEntries = useMemo(() => {
    if (searchFilterUserIds === null) {
      return entries
    }

    return entries.filter(entry => searchFilterUserIds.includes(entry.userId))
  }, [entries, searchFilterUserIds])

  const summaries = useMemo<EmployeeSummary[]>(() => {
    const summaryMap = visibleEntries.reduce<Record<string, EmployeeSummaryAccumulator>>((accumulator, entry) => {
      const currentSummary = accumulator[entry.userId] || {
        userId: entry.userId,
        employee: employeeMap[entry.userId] || null,
        totalSeconds: 0,
        entriesCount: 0,
        daysCount: 0,
        lastEntryAt: null,
        lastLocationType: null,
        workDates: new Set<string>()
      }
      const isNewerEntry =
        !currentSummary.lastEntryAt ||
        new Date(entry.startedAt).getTime() > new Date(currentSummary.lastEntryAt).getTime()
      currentSummary.workDates.add(entry.workDate)

      accumulator[entry.userId] = {
        ...currentSummary,
        employee: employeeMap[entry.userId] || currentSummary.employee,
        totalSeconds: currentSummary.totalSeconds + Number(entry.workedSeconds || 0),
        entriesCount: currentSummary.entriesCount + 1,
        daysCount: currentSummary.workDates.size,
        lastEntryAt: isNewerEntry ? entry.startedAt : currentSummary.lastEntryAt,
        lastLocationType: isNewerEntry ? entry.locationType || null : currentSummary.lastLocationType
      }

      return accumulator
    }, {})

    return Object.values(summaryMap)
      .map(summary => {
        const { workDates, ...employeeSummary } = summary
        void workDates

        return {
          ...employeeSummary,
          employee: summary.employee || createFallbackEmployee(summary.userId)
        }
      })
      .sort((left, right) => right.totalSeconds - left.totalSeconds)
  }, [employeeMap, visibleEntries])

  const selectedEmployeeEntries = useMemo(() => {
    if (!selectedEmployee?._id) {
      return []
    }

    return entries.filter(entry => entry.userId === selectedEmployee._id)
  }, [entries, selectedEmployee])

  useEffect(() => {
    if (!selectedEmployeeEntries.length || !cocap) {
      setSelectedEmployeePausesByEntryId({})
      setLoadingSelectedEmployeePauses(false)
      return undefined
    }

    let isMounted = true
    const entryIds = selectedEmployeeEntries.map(entry => entry.entryId).filter(Boolean)

    const loadPauses = async () => {
      setLoadingSelectedEmployeePauses(true)

      try {
        const response = await getAdminMetricsPausesService(entryIds, cocap)
        if (!isMounted) {
          return
        }

        setSelectedEmployeePausesByEntryId(response.pausesByEntryId || {})
      } catch (pauseError) {
        if (!isMounted) {
          return
        }

        console.warn('Admin metrics pauses lookup failed', pauseError)
        setSelectedEmployeePausesByEntryId({})
      } finally {
        if (isMounted) {
          setLoadingSelectedEmployeePauses(false)
        }
      }
    }

    void loadPauses()

    return () => {
      isMounted = false
    }
  }, [cocap, selectedEmployeeEntries])

  const totalSeconds = useMemo(
    () => visibleEntries.reduce((accumulator, entry) => accumulator + Number(entry.workedSeconds || 0), 0),
    [visibleEntries]
  )

  const averageSecondsPerEmployee = useMemo(() => {
    if (!summaries.length) {
      return 0
    }

    return Math.round(totalSeconds / summaries.length)
  }, [summaries.length, totalSeconds])

  const hasActiveSearch = useMemo(() => Boolean(selectedEmployee?._id) || searchFilterUserIds !== null, [
    searchFilterUserIds,
    selectedEmployee
  ])

  const selectedPeriodLabel = useMemo(() => {
    if (mode === 'day') {
      return referenceDate
    }

    if (mode === 'week') {
      const weekStartDate = getWeekStartDate(referenceDate)
      const monday = new Date(`${weekStartDate}T12:00:00-05:00`)
      const saturday = new Date(monday.getTime())
      saturday.setUTCDate(saturday.getUTCDate() + 5)
      return `${weekStartDate} - ${formatLocalDateKey(saturday)}`
    }

    return referenceMonth
  }, [mode, referenceDate, referenceMonth])

  return {
    bootstrapping,
    bootstrapReady,
    entries,
    error,
    formatSeconds,
    hasActiveSearch,
    loadingEntries,
    loadingSelectedEmployeePauses,
    mode,
    referenceDate,
    referenceMonth,
    resetSearch,
    searchEmployees,
    searchTerm,
    searching,
    selectEmployee,
    selectedEmployee,
    selectedEmployeeEntries,
    selectedEmployeePausesByEntryId,
    selectedPeriodLabel,
    setMode,
    setReferenceDate,
    setReferenceMonth,
    setSearchTerm,
    summaries,
    averageSecondsPerEmployee,
    visibleEntriesCount: visibleEntries.length,
    totalSeconds
  }
}

export default useAdminHoursMetrics
